// app/javascript/controllers/dae20/live_block_controller.js
import { Controller } from "@hotwired/stimulus"
import { createConsumer } from "@rails/actioncable"

/**
 * Lightweight live-reload controller for custom block components.
 *
 * Unlike live_table_controller (which replaces individual rows via PK),
 * this controller triggers a Turbo page reload (soft visit) whenever
 * a rows_changed signal is received on any subscribed ActionCable channel.
 *
 * Usage:
 * <div data-controller="dae20--live-block"
 *      data-dae20--live-block-source-codes-value='["b2-exch_orders-ist"]'
 *      data-dae20--live-block-debounce-value="1000">
 *   ...block content...
 * </div>
 */
export default class extends Controller {
  static values = {
    sourceCodes: { type: Array, default: [] },
    debounce: { type: Number, default: 1000 },
    gracePeriod: { type: Number, default: 3000 }
  }

  subscriptions = []
  consumer = null
  debounceTimer = null
  connectedAt = null

  connect() {
    if (this.sourceCodesValue.length === 0) {
      console.warn("[LiveBlock] No sourceCodes — skipping subscription")
      return
    }

    this.connectedAt = Date.now()
    this.consumer = createConsumer()

    this.sourceCodesValue.forEach(code => {
      const sub = this.consumer.subscriptions.create(
        { channel: "Dae20::LiveTableChannel", source_code: code },
        {
          connected: () => console.log("[LiveBlock] Subscribed to", code),
          received: (data) => this.handleReceived(data)
        }
      )
      this.subscriptions.push(sub)
    })
  }

  disconnect() {
    this.subscriptions.forEach(sub => sub.unsubscribe())
    this.subscriptions = []
    if (this.debounceTimer) clearTimeout(this.debounceTimer)
    this.connectedAt = null
  }

  handleReceived(data) {
    if (data.type !== "rows_changed") return

    if (this.connectedAt && (Date.now() - this.connectedAt) < this.gracePeriodValue) {
      console.log("[LiveBlock] Grace period — skipping initial sync event")
      return
    }

    console.log("[LiveBlock] Received change, scheduling reload", data)

    if (this.debounceTimer) clearTimeout(this.debounceTimer)
    this.debounceTimer = setTimeout(() => this.reloadPage(), this.debounceValue)
  }

  reloadPage() {
    if (typeof Turbo !== "undefined" && Turbo.visit) {
      Turbo.visit(window.location.href, { action: "replace" })
    } else {
      window.location.reload()
    }
  }
}
